import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';

import { getNarration } from '../api/speechAPI';
import type { AudioRequest } from '../types/audioRequestType';
import type { ActiveAudioContent } from '../types/activeAudioContent';

export interface AudioPlaybackState {
	activeContent?: ActiveAudioContent;
	isLoading: boolean;
	isPlaying: boolean;
	isClosed: boolean;
	isEnded: boolean;
	playbackSpeed: number;
	error?: Error;
	stream?: ReadableStream<Uint8Array>;
}

type AudioPlaybackStateContainerProps = {
	initialAudioPlaybackState?: Partial<AudioPlaybackState>;
};

type AudioPlaybackActions = {
	initialize: (initialState: Partial<AudioPlaybackState>) => Action<AudioPlaybackState, void, void>;
	updateActiveContent: (
		activeContent: ActiveAudioContent,
	) => Action<AudioPlaybackState, void, void>;
	onLoadStart: (activeContent: ActiveAudioContent) => Action<AudioPlaybackState, void, void>;
	onLoadEnd: () => Action<AudioPlaybackState, void, void>;
	onPlayContent: (
		audioRequest: AudioRequest,
		activeContent: ActiveAudioContent,
	) => Action<AudioPlaybackState, void, void>;
	onContentPaused: () => Action<AudioPlaybackState, void, void>;
	onContentResumed: () => Action<AudioPlaybackState, void, void>;
	onContentPlaybackSpeedChanged: (speed: number) => Action<AudioPlaybackState, void, void>;
	onContentStopped: () => Action<AudioPlaybackState, void, void>;
	onContentEnded: () => Action<AudioPlaybackState, void, void>;
	onContentError: (error: Error) => Action<AudioPlaybackState, void, void>;
};

export const audioPlaybackActions: AudioPlaybackActions = {
	initialize:
		(initialState: Partial<AudioPlaybackState> = {}) =>
		async ({ setState }) => {
			const emptyAudioPlaybackState: AudioPlaybackState = {
				stream: undefined,
				activeContent: undefined,
				isLoading: false,
				isPlaying: false,
				isClosed: false,
				isEnded: false,
				error: undefined,
				playbackSpeed: Number(localStorage.getItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED)) || 1,
			};
			setState(Object.assign(emptyAudioPlaybackState, initialState));
		},
	updateActiveContent:
		(activeContent: ActiveAudioContent) =>
		({ setState, getState }) => {
			const { activeContent: activeContentFromState } = getState();
			if (activeContent) {
				setState({
					activeContent: {
						...activeContentFromState,
						...activeContent,
					},
				});
			}
		},
	onLoadStart:
		(activeContent: ActiveAudioContent) =>
		({ setState, getState }) => {
			const { activeContent: activeContentFromState } = getState();
			setState({
				activeContent: {
					...activeContentFromState,
					...activeContent,
				},
				isLoading: true,
			});
		},
	onLoadEnd:
		() =>
		({ setState }) => {
			setState({ isLoading: false });
		},
	onPlayContent:
		(audioRequest: AudioRequest, activeContent: ActiveAudioContent) =>
		async ({ getState, setState, dispatch }) => {
			dispatch(audioPlaybackActions.initialize({}));
			dispatch(audioPlaybackActions.onLoadStart(activeContent));

			try {
				// TODO: Temporarily subbing in narrations and summary for briefings
				// in a future PR we will allow the consumer of this hook to decide which
				// endpoint to use

				// const stream = await getSpeech(audioRequest);
				const stream = await getNarration(audioRequest);

				if (getState().isClosed) {
					return;
				}

				const { activeContent: activeContentFromState } = getState();
				setState({
					activeContent: {
						...activeContentFromState,
						...activeContent,
					},
					isPlaying: false,
					isEnded: false,
					stream,
				});

				return stream;
			} catch (e) {
				setState({
					error: e,
					activeContent: undefined,
					stream: undefined,
				});
				throw e;
			} finally {
				dispatch(audioPlaybackActions.onLoadEnd());
			}
		},
	onContentPaused:
		() =>
		({ setState }) => {
			setState({
				isPlaying: false,
			});
		},
	onContentResumed:
		() =>
		({ setState }) => {
			setState({
				isPlaying: true,
				isEnded: false,
			});
		},
	onContentPlaybackSpeedChanged:
		(speed: number) =>
		({ setState }) => {
			localStorage.setItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED, speed);
			setState({ playbackSpeed: speed });
		},
	onContentStopped:
		() =>
		async ({ dispatch, setState }) => {
			dispatch(audioPlaybackActions.initialize({}));
			setState({ isClosed: true });
		},
	onContentEnded:
		() =>
		({ setState }) => {
			setState({
				isEnded: true,
				isPlaying: false,
			});
		},
	onContentError:
		(error: Error) =>
		({ setState }) => {
			setState({
				error,
			});
		},
};

const Store = createStore<AudioPlaybackState, AudioPlaybackActions>({
	initialState: {
		activeContent: undefined,
		isLoading: false,
		isPlaying: false,
		isClosed: false,
		isEnded: false,
		playbackSpeed: 1,
		error: undefined,
	},
	actions: audioPlaybackActions,
	name: 'listenerState',
});

export const AudioPlaybackStateContainer = createContainer<
	AudioPlaybackState,
	AudioPlaybackActions,
	AudioPlaybackStateContainerProps
>(Store, {
	onInit:
		() =>
		async ({ dispatch }, { initialAudioPlaybackState }) => {
			void dispatch(audioPlaybackActions.initialize(initialAudioPlaybackState ?? {}));
		},
});

export const useAudioPlaybackState = createHook<AudioPlaybackState, AudioPlaybackActions>(Store);
export const useAudioPlaybackStateActions = createActionsHook(Store);
